/**
 * E-GTCC-VIEW-DPLANE1 — tab "Health Center".
 *
 * This is the `grid-main` of `GTCC_REFERENCE_2026-09-01.html:921-955`,
 * element for element: left the panels "Tenant-Platzierung" and
 * "Backups + Retention je Datastore", right the panels "Inventar" and
 * "Plan Builders".
 *
 * Every value is measured. The reference prints sample numbers
 * ("Beispieldaten"); this view prints what the endpoints report and renders
 * "nicht messbar" where they report nothing (Owner decision P10). Nothing
 * here executes: the [Move-Plan (dry-run)] and [Validate] affordances are
 * links into the Plan Builders tab, not actions.
 */
import { useRef, useState, type ReactNode } from "react";
import { EmptyState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";

import {
  renderEvidenceBundle,
  verifyEvidenceBundle,
} from "../../../../data-plane/evidence-bundle/api.js";
import type {
  BackupHealth,
  DataPlaneHealthOverview,
  Datastore,
  TelemetryRetentionPolicy,
  TenantPlacement,
} from "../../../../data-plane/types.js";
import type {
  DbOpsDatabase,
  DbOpsHostsByRole,
  DbOpsOverview,
  PostgresInstance,
} from "../../../../db-ops/types.js";
import {
  fetchDplaneDatabases,
  fetchDplaneDbOpsOverview,
  fetchDplaneInstances,
  fetchDplaneOverview,
  fetchDplanePlans,
  DPLANE_DATABASES_KEY,
  DPLANE_DBOPS_OVERVIEW_KEY,
  DPLANE_INSTANCES_KEY,
  DPLANE_OVERVIEW_KEY,
  DPLANE_PLANS_KEY,
  type DbOpsPersistedPlan,
} from "../../../api/dplane.js";
import {
  FleetDot,
  GTCC_VISUALLY_HIDDEN,
  NotMeasurable,
  type FleetTone,
} from "../../cockpit/components/FleetPrimitives.js";
import { Row, type CockpitTone } from "../../../ui/primitives.js";
import { ControlTextAction } from "../../control/ControlPrimitives.js";
import {
  asList,
  DplaneGate,
  DplanePanel,
  DPLANE_QUERY_OPTIONS,
  GRID_COLUMN_STYLE,
  GRID_MAIN_STYLE,
  HOST_STYLE,
  MONO_STYLE,
  Mono,
  ROW_ACTION_STYLE,
  ROW_LABEL_STYLE,
  ROW_LINE_STYLE,
  ROW_VALUE_STYLE,
  ROW_VALUE_TEXT_STYLE,
  SUB_STYLE,
  TABLE_CELL_STYLE,
  TABLE_HEAD_STYLE,
  TABLE_STYLE,
  TABLE_WRAP_STYLE,
  type DplaneTabProps,
} from "../components/DplanePanels.js";
import {
  toneOfBackup,
  toneOfCleanup,
  toneOfPlacement,
  toneOfRestoreDrill,
  toneOfVpnHealth,
  worstTone,
} from "../components/dplaneTone.js";

/* ------------------------------------------------------------- helpers */

/**
 * `FleetRow` (`FleetPrimitives.tsx`) carries no `testId` prop, and its
 * internal `Dot` (`ui/primitives.tsx`) carries no `data-tone` either — both
 * are asserted by this file's tests (`toneOfRow`). The wrapping `div` below
 * adds the missing `data-testid`, and the label is wrapped in a plain
 * (unstyled) span that carries `data-tone` as the descendant the tests query
 * for, without bending the shared primitive itself. Same call-site
 * adaptation as `InfraTab.tsx`'s `InfraRow`.
 *
 * A-GTCC-FIX-DPLANE1: Name und Wert stehen nicht mehr als zwei Flex-Elemente
 * direkt in `.row`, sondern in einem eigenen Kasten (`ROW_LINE_STYLE`) mit
 * `minWidth: 0`. Nur so darf der Wert schrumpfen; vorher zwang seine
 * Inhaltsbreite die Zeile in der schmalen rechten Tafelspalte auf zwei bis
 * drei Zeilen (Befund "Inventory"/"Plan Builders" in ABWEICHUNGEN.md).
 * `Row` bleibt die gemeinsame Primitive — sie zeichnet `.row` und den Punkt.
 */
function FleetRow(props: {
  tone: FleetTone;
  label: ReactNode;
  value: ReactNode;
  testId?: string;
}): ReactNode {
  return (
    <div data-testid={props.testId}>
      <Row tone={props.tone as CockpitTone}>
        <span style={ROW_LINE_STYLE}>
          <span data-tone={props.tone} style={ROW_LABEL_STYLE}>
            {props.label}
          </span>
          <span style={ROW_VALUE_STYLE}>{props.value}</span>
        </span>
      </Row>
    </div>
  );
}

/** The entry whose status is worst; ties keep the server's order. */
function worstBy<T>(items: ReadonlyArray<T>, tone: (item: T) => FleetTone): T | undefined {
  const rank: Readonly<Record<FleetTone, number>> = { ok: 0, info: 1, muted: 2, warn: 3, crit: 4 };
  return items.reduce<T | undefined>(
    (worst, item) => (worst === undefined || rank[tone(item)] > rank[tone(worst)] ? item : worst),
    undefined,
  );
}

/** Distinct values in first-seen order — the reference joins them with "·". */
function distinct(values: ReadonlyArray<string>): ReadonlyArray<string> {
  return [...new Set(values)];
}

/** Midnight of the local calendar day `date` falls into. */
function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * A-GTCC-FIX-DPLANE1, Punkt 4 — "Backup-Alter max." als Tageswort plus Uhrzeit.
 *
 * Die Referenz schreibt `heute 03:30`
 * (`GTCC_REFERENCE_2026-09-01.html:934`), die View schrieb ein volles Datum
 * (`01.09.26, 05:30`). Der Abstand ist der ganze Punkt der Zeile: ein Backup
 * von heute Nacht liest sich anders als eines von vorgestern, und das Datum
 * verlangt vom Leser die Rechnung.
 *
 * Gemessen bleibt derselbe Zeitstempel; nur der Kalendertag wird zum Wort.
 * `Intl.RelativeTimeFormat` mit `numeric: "auto"` liefert die Woerter der
 * Sprache selbst ("heute", "gestern", "vor 3 Tagen") — derselbe Weg wie in
 * `GtccSupportInboxView.tsx:77`. Der Abstand zaehlt Kalendertage, nicht
 * 24-Stunden-Bloecke: ein Backup um 03:30 ist heute Nacht gelaufen, auch wenn
 * es dann nur sechs Stunden her ist. Die Uhrzeit bleibt die lokale Uhrzeit des
 * gemessenen Zeitpunkts.
 */
export function formatBackupMoment(iso: string, locale: string, now: Date): string {
  const when = new Date(iso);
  const days = Math.round((startOfLocalDay(when) - startOfLocalDay(now)) / 86_400_000);
  const day = new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(days, "day");
  const time = new Intl.DateTimeFormat(locale, { timeStyle: "short" }).format(when);
  return `${day} ${time}`;
}

/* ------------------------------------------------- panel: Tenant-Platzierung */

/**
 * A-GTCC-FIX-DPLANE1, Punkt 3 — die Spalte TELEMETRY.
 *
 * Die Referenz schreibt dort `dediziert`, nicht den Hostnamen
 * (`GTCC_REFERENCE_2026-09-01.html:927`). Das ist kein zweiter Wert, sondern
 * derselbe: `telemetryStoreId` zeigt auf einen Datastore, und dieser Datastore
 * traegt sein `tenancyModel` (`data-plane/types.ts:56,80`). Die Spalte
 * beschriftet also das gemessene Mietmodell des Telemetry-Stores.
 *
 * Nennt die Quelle einen Store, den die Datastore-Liste nicht enthaelt, gibt
 * sie das Mietmodell nicht her; dann bleibt die Kennung stehen, statt ein
 * Modell zu raten (Owner-Entscheid P8/P9: nie ein Wert, den es nicht gibt).
 */
function telemetryCellValue(
  placement: TenantPlacement,
  datastores: ReadonlyArray<Datastore>,
  label: (key: string) => string,
): string {
  const store = datastores.find((entry) => entry.id === placement.telemetryStoreId);
  if (store === undefined) return placement.telemetryStoreId;
  return label(`admin.gtcc.dplane.tenancy_model.${store.tenancyModel}`);
}

function PlacementTable(props: {
  placements: ReadonlyArray<TenantPlacement>;
  datastores: ReadonlyArray<Datastore>;
  onMovePlan: (tenantId: string) => void;
}): ReactNode {
  const { t } = useLocale();
  if (props.placements.length === 0) {
    /*
     * Eine leere Liste ist gemessen: der Server hat geantwortet, und es ist
     * keine Tenant-Platzierung eingetragen. Das ist etwas anderes als "nicht
     * messbar" (dafuer steht `NotMeasurable`, wenn eine Zahl fehlt), darum
     * hier der leere Zustand mit eigenem Text.
     */
    return (
      <div data-testid="gtcc-dplane-placement-empty">
        <EmptyState title={t("admin.gtcc.dplane.health.placement.empty")} />
      </div>
    );
  }
  return (
    <div style={TABLE_WRAP_STYLE}>
      <table style={TABLE_STYLE} data-testid="gtcc-dplane-placement-table">
        {/*
         * Die Referenz schreibt ueber der Tabelle keine Bildunterschrift
         * (`GTCC_REFERENCE_2026-09-01.html:925`), die Tabellen-Inventur des
         * Repos verlangt aber je Tabelle eine. Darum bleibt sie im Baum und
         * verlaesst nur die Flaeche — derselbe Weg wie bei den stummen
         * Spaltenkoepfen (`FleetPrimitives.tsx:438`).
         */}
        <caption style={GTCC_VISUALLY_HIDDEN}>
          {t("admin.gtcc.dplane.health.placement.caption")}
        </caption>
        <thead>
          <tr>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.health.placement.col.tenant")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.health.placement.col.primary_db")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.health.placement.col.telemetry")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.health.placement.col.layout")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.health.placement.col.status")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.health.placement.col.move")}
            </th>
          </tr>
        </thead>
        <tbody>
          {/*
           * Liefert die Quelle eine Zeile ohne `tenantId`, waere `key`
           * undefiniert und React verlangte einen Schluessel. Die Zeile bleibt
           * trotzdem sichtbar; der Listenindex traegt sie dann (Owner-Entscheid
           * P10: nie ein Absturz und keine Warnung statt einer Aussage).
           */}
          {props.placements.map((row, index) => (
            <tr
              key={row.tenantId ?? `placement-${index}`}
              data-testid={`gtcc-dplane-placement-row-${row.tenantId}`}
            >
              {/*
               * Die Referenz schreibt die TENANT-Spalte als `td`; auch jede
               * andere GTCC-Tabelle kommt ohne Zeilenkopf aus
               * (`gtcc-table-semantics.test.tsx:1446`). Darum `td` statt
               * `th scope="row"`.
               */}
              <td style={{ ...TABLE_CELL_STYLE, ...HOST_STYLE }}>{row.tenantDisplayName}</td>
              <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>{row.primaryDbId}</td>
              <td
                style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}
                data-testid={`gtcc-dplane-placement-telemetry-${row.tenantId}`}
              >
                {telemetryCellValue(row, props.datastores, t)}
              </td>
              <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>{row.layout}</td>
              <td style={{ ...TABLE_CELL_STYLE, whiteSpace: "nowrap" }}>
                <span
                  data-testid={`gtcc-dplane-placement-status-${row.tenantId}`}
                  data-tone={toneOfPlacement(row.placementStatus)}
                >
                  {/*
                   * `<span class="dot ok"></span>Stable` — die STATUS-Spalte
                   * der Referenz traegt den Statuspunkt vor dem Wort
                   * (`GTCC_REFERENCE_2026-09-01.html:927`). Der Punkt ist
                   * `aria-hidden`, die Farbe wiederholt nur den Text.
                   */}
                  <FleetDot tone={toneOfPlacement(row.placementStatus)} />
                  {t(`admin.gtcc.dplane.placement_status.${row.placementStatus}`)}
                </span>
              </td>
              <td style={TABLE_CELL_STYLE}>
                {/*
                 * E-GTCC-VIEW-DPLANE-GW-HW1, Ursache 2 (Zeilenraster): hier stand
                 * ein `Button` des Design-Systems. Seine Mindesthoehe von 32 px
                 * (`--gt-cmp-button-min-block-size-sm`) hat die Zeile auf 47 px
                 * getrieben, wo die Referenz 34 px setzt (gemessen mit
                 * `getBoundingClientRect`, hell, 1600x900). Die Referenz schreibt
                 * die Aktion als `td.mono`
                 * (`GTCC_REFERENCE_2026-09-01.html:927`); dafuer haelt
                 * `ControlPrimitives.tsx` `ControlTextAction`. Die Klammern
                 * stehen schon im Katalogtext, deshalb ohne `brackets`.
                 */}
                <ControlTextAction
                  label={t("admin.gtcc.dplane.health.placement.move_plan")}
                  onActivate={() => props.onMovePlan(row.tenantId)}
                  testId={`gtcc-dplane-placement-move-${row.tenantId}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------------------- panel: Backups + Retention je Datastore */

function backupValue(
  backups: ReadonlyArray<BackupHealth>,
  format: (iso: string) => string,
  label: (key: string, params?: Record<string, string | number>) => string,
): ReactNode {
  const worst = worstBy(backups, (entry) => toneOfBackup(entry.status));
  if (worst === undefined) return <NotMeasurable testId="gtcc-dplane-backup-age-unmeasured" />;
  if (worst.lastBackupAt === null) {
    return <NotMeasurable testId="gtcc-dplane-backup-age-unmeasured" />;
  }
  return (
    <Mono testId="gtcc-dplane-backup-age-value" style={ROW_VALUE_TEXT_STYLE}>
      {label("admin.gtcc.dplane.health.backup.age_value", {
        measuredAt: format(worst.lastBackupAt),
        // Der Referenzwert ist "Plan Pro" — das Plan-Enum wird beschriftet,
        // nicht roh ausgegeben (`GTCC_REFERENCE_2026-09-01.html:936`).
        plan: label(`admin.gtcc.dplane.plan.${worst.plan}`),
        days: worst.backupRetentionDays,
      })}
    </Mono>
  );
}

function retentionValue(
  policies: ReadonlyArray<TelemetryRetentionPolicy>,
  label: (key: string, params?: Record<string, string | number>) => string,
): ReactNode {
  if (policies.length === 0) {
    return <NotMeasurable testId="gtcc-dplane-retention-unmeasured" />;
  }
  const months = distinct(policies.map((policy) => String(policy.configuredMonths))).join(" / ");
  return (
    <Mono testId="gtcc-dplane-retention-value" style={ROW_VALUE_TEXT_STYLE}>
      {label("admin.gtcc.dplane.health.retention.value", {
        months,
        max: policies[0]!.maxMonths,
      })}
    </Mono>
  );
}

/**
 * A-GTCC-FIX-DPLANE1, Punkt 5 — "Restore-Drill" in der Form der Referenz.
 *
 * Die Referenz nennt die Datastores, nicht die Haeufigkeit ihrer Zustaende:
 * `Keycloak ✓ · Tenant-Stores: offen` (`GTCC_REFERENCE_2026-09-01.html:936`).
 * Vorher stand hier eine Strichliste (`bestanden: 1 · überfällig: 1`), die
 * verschweigt, WELCHER Store offen ist — genau die Frage, wegen der die Zeile
 * existiert. Der bestandene Drill bekommt das Haekchen der Referenz, jeder
 * andere seinen gemessenen Zustand aus `RestoreDrillStatus`. Die Zustandswoerter
 * bleiben die Namen des Enums (`überfällig` fuer `overdue`), weil die Referenz
 * an dieser Stelle Beispieldaten schreibt und `offen` den Zustand nicht so
 * genau benennt wie die Quelle ihn misst.
 *
 * Der Name ist der `displayName` des Datastores; fehlt der Datastore in der
 * Liste, traegt die Kennung die Zeile (P8/P9: kein erfundener Name).
 */
function restoreDrillValue(
  backups: ReadonlyArray<BackupHealth>,
  datastores: ReadonlyArray<Datastore>,
  label: (key: string, params?: Record<string, string | number>) => string,
): ReactNode {
  if (backups.length === 0) {
    return <NotMeasurable testId="gtcc-dplane-restore-drill-unmeasured" />;
  }
  const text = backups
    .map((entry) => {
      const store =
        datastores.find((candidate) => candidate.id === entry.datastoreId)?.displayName ??
        entry.datastoreId;
      return entry.restoreDrillStatus === "passed"
        ? label("admin.gtcc.dplane.health.restore_drill.passed", { store })
        : label("admin.gtcc.dplane.health.restore_drill.open", {
            store,
            status: label(`admin.gtcc.dplane.restore_drill_status.${entry.restoreDrillStatus}`),
          });
    })
    .join(" · ");
  return (
    <Mono testId="gtcc-dplane-restore-drill-value" style={ROW_VALUE_TEXT_STYLE}>
      {text}
    </Mono>
  );
}

/**
 * The reference's "Evidence-Bundle-Prüfung": paste JSON, [Verify],
 * [Render markdown]. It drives the verifier of the old console
 * (`data-plane/evidence-bundle/api.ts`) — the same two dry-run routes, no
 * third one. The existing `EvidenceBundleReviewPanel` is not embedded
 * because it renders its own `h2` and a GTCC view owns exactly one
 * level-1/2 heading (`gtcc-a11y-smoke.test.tsx:287`).
 */
function EvidenceBundleRow(): ReactNode {
  const { t } = useLocale();
  const [bundle, setBundle] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * A-GTCC-FIX-DPLANE1 — die Referenz zeigt genau eine Zeile:
   * "Evidence-Bundle-Prüfung  JSON einfügen → [Verify] [Render markdown]"
   * (`GTCC_REFERENCE_2026-09-01.html:939`). Das Eingabefeld gehoert zur
   * Zeile, steht dort aber nicht offen — es klappt beim ersten Klick auf
   * eine der beiden Schaltflaechen auf. Die Pruefung selbst bleibt
   * unveraendert: ohne Text wird nichts gesendet, der erste Klick oeffnet
   * nur und setzt den Schreibpunkt ins Feld.
   */
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  /** Erst oeffnen, dann pruefen: gibt `true` zurueck, wenn nur geoeffnet wurde. */
  function openedInstead(): boolean {
    if (open) return false;
    setOpen(true);
    // Der Fokus wandert ins Feld, sobald React es gezeichnet hat.
    window.requestAnimationFrame(() => inputRef.current?.focus());
    return true;
  }

  function parsed(): unknown | undefined {
    try {
      return JSON.parse(bundle) as unknown;
    } catch {
      setStatus(t("admin.gtcc.dplane.health.evidence.parse_error"));
      setMarkdown(null);
      return undefined;
    }
  }

  async function onVerify(): Promise<void> {
    if (openedInstead()) return;
    const body = parsed();
    if (body === undefined) return;
    setBusy(true);
    const result = await verifyEvidenceBundle(body);
    setBusy(false);
    setMarkdown(null);
    setStatus(
      result.ok
        ? t("admin.gtcc.dplane.health.evidence.verified", {
            status: result.data.verification.status,
            problems: result.data.verification.summary.problemCount,
            warnings: result.data.verification.summary.warningCount,
          })
        : t(`admin.gtcc.dplane.health.evidence.error.${result.error.kind}`),
    );
  }

  async function onRender(): Promise<void> {
    if (openedInstead()) return;
    const body = parsed();
    if (body === undefined) return;
    setBusy(true);
    const result = await renderEvidenceBundle(body);
    setBusy(false);
    if (result.ok) {
      setMarkdown(result.data.markdown);
      setStatus(null);
      return;
    }
    setMarkdown(null);
    setStatus(t(`admin.gtcc.dplane.health.evidence.error.${result.error.kind}`));
  }

  return (
    <Stack gap={2}>
      <FleetRow
        tone="info"
        label={t("admin.gtcc.dplane.health.evidence.label")}
        value={
          <>
            <Mono style={ROW_VALUE_TEXT_STYLE}>{t("admin.gtcc.dplane.health.evidence.hint")}</Mono>
            <span style={ROW_ACTION_STYLE}>
              <ControlTextAction
                label={t("admin.gtcc.dplane.health.evidence.verify")}
                /*
                 * Geschlossen oeffnet der Klick nur das Feld — darum ist die
                 * Aktion dann nie gesperrt. Erst im geoeffneten Zustand gilt
                 * wieder: ohne Text keine Pruefung.
                 */
                disabled={busy || (open && bundle.trim() === "")}
                expanded={open}
                controls="gtcc-dplane-evidence-input"
                onActivate={() => void onVerify()}
                testId="gtcc-dplane-evidence-verify"
              />
            </span>
            <span style={ROW_ACTION_STYLE}>
              <ControlTextAction
                label={t("admin.gtcc.dplane.health.evidence.render")}
                disabled={busy || (open && bundle.trim() === "")}
                expanded={open}
                controls="gtcc-dplane-evidence-input"
                onActivate={() => void onRender()}
                testId="gtcc-dplane-evidence-render"
              />
            </span>
          </>
        }
        testId="gtcc-dplane-evidence-row"
      />
      {!open ? null : (
        <>
          <label htmlFor="gtcc-dplane-evidence-input" style={SUB_STYLE}>
            {t("admin.gtcc.dplane.health.evidence.input_label")}
          </label>
          <textarea
            id="gtcc-dplane-evidence-input"
            data-testid="gtcc-dplane-evidence-input"
            ref={inputRef}
            rows={4}
            value={bundle}
            onChange={(event) => setBundle(event.target.value)}
            style={{
              width: "100%",
              fontFamily: "var(--gt-sys-font-family-mono)",
              fontSize: "var(--gt-sys-font-size-xs)",
              color: "var(--gt-sys-text)",
              background: "var(--gt-sys-surface)",
              border: "1px solid var(--gt-sys-border)",
              borderRadius: "var(--gt-sys-radius-sm)",
              padding: "var(--gt-sys-space-2)",
            }}
          />
        </>
      )}
      {status === null ? null : (
        <p style={SUB_STYLE} data-testid="gtcc-dplane-evidence-status" role="status">
          {status}
        </p>
      )}
      {markdown === null ? null : (
        <pre
          data-testid="gtcc-dplane-evidence-markdown"
          style={{ ...MONO_STYLE, margin: 0, whiteSpace: "pre-wrap" }}
        >
          {markdown}
        </pre>
      )}
    </Stack>
  );
}

/* ------------------------------------------------------- panel: Inventar */

function InventoryRows(props: {
  dbOps: DbOpsOverview;
  instances: ReadonlyArray<PostgresInstance>;
  databases: ReadonlyArray<DbOpsDatabase>;
  overview: DataPlaneHealthOverview | undefined;
}): ReactNode {
  const { t } = useLocale();
  // `hostsByRole` ist im DTO Pflicht, kann in Testdoubles aber fehlen; darum
  // die Undefined-Annahme statt eines Casts ueber `Record<string, number>`.
  const byRole: DbOpsHostsByRole | undefined = props.dbOps.hostsByRole;
  /*
   * E-GTCC-DPLANE-TEN-1ZU1 — die Klammer der Inventar-Zeile ist ein
   * `.mono`-Wert, kein Fliesstext: die Referenz schreibt dort die kurzen
   * Rollen-Marken der Datenebene, klein und ohne Beiwort
   * (`primary·replica·monitor·backup·runner·extern`,
   * `GTCC_REFERENCE_2026-09-01.html:942`). Die ausgeschriebenen Rollennamen
   * (`admin.gtcc.dplane.host_role.*`) bleiben, wo sie hingehoeren: in der
   * Spalte ROLLE der Inventar-Tabelle (`InventoryTab.tsx:142`). Darum ein
   * eigener, sprachunabhaengiger Marken-Satz fuer diese Zeile.
   */
  const roleText =
    byRole === undefined
      ? undefined
      : Object.entries(byRole)
          .filter(([, count]) => count > 0)
          .map(([role]) => t(`admin.gtcc.dplane.inv.host_role.${role}`))
          .join("·");
  const hostCount = props.dbOps.counts?.hostCount;

  const versions = distinct(props.instances.map((instance) => instance.postgresVersion));
  const scopes = distinct(props.instances.map((instance) => instance.bindScopeLabel));

  const named = props.databases
    .filter((database) => database.purpose !== "tenant_dedicated")
    .map((database) => database.name);
  const dedicated = props.databases.filter(
    (database) => database.purpose === "tenant_dedicated",
  ).length;
  const databaseText = [
    ...named,
    ...(dedicated > 0
      ? [t("admin.gtcc.dplane.inv.databases.dedicated", { count: dedicated })]
      : []),
  ].join(" · ");

  const vpn = asList(props.overview?.vpn);
  const vpnTone = worstTone(vpn.map((entry) => toneOfVpnHealth(entry.status)));
  const vpnHealthy = vpn.filter((entry) => entry.status === "healthy").length;

  return (
    <Stack gap={0}>
      <FleetRow
        tone={hostCount === undefined ? "muted" : "ok"}
        label={t("admin.gtcc.dplane.inv.hosts")}
        value={
          hostCount === undefined || roleText === undefined ? (
            <NotMeasurable testId="gtcc-dplane-inv-hosts-unmeasured" />
          ) : (
            <Mono testId="gtcc-dplane-inv-hosts-value" style={ROW_VALUE_TEXT_STYLE}>
              {/*
               * Ohne einen einzigen Host gibt es keine Rollenaufteilung. Die
               * Referenz schreibt "5 (1 primary · 1 replica)"; eine leere
               * Klammer waere kein gemessener Wert, sondern ein Rest der
               * Vorlage, darum dann nur die Zahl.
               */}
              {roleText === ""
                ? t("admin.gtcc.dplane.inv.hosts.value_plain", { count: hostCount })
                : t("admin.gtcc.dplane.inv.hosts.value", { count: hostCount, roles: roleText })}
            </Mono>
          )
        }
        testId="gtcc-dplane-inv-hosts"
      />
      <FleetRow
        tone={props.instances.length === 0 ? "muted" : worstTone(["ok"])}
        label={t("admin.gtcc.dplane.inv.instances")}
        value={
          props.instances.length === 0 ? (
            <NotMeasurable testId="gtcc-dplane-inv-instances-unmeasured" />
          ) : (
            <Mono testId="gtcc-dplane-inv-instances-value" style={ROW_VALUE_TEXT_STYLE}>
              {t("admin.gtcc.dplane.inv.instances.value", {
                count: props.instances.length,
                versions: versions.join(" / "),
                scopes: scopes.join(" + "),
              })}
            </Mono>
          )
        }
        testId="gtcc-dplane-inv-instances"
      />
      <FleetRow
        tone={props.databases.length === 0 ? "muted" : "ok"}
        label={t("admin.gtcc.dplane.inv.databases")}
        value={
          props.databases.length === 0 ? (
            <NotMeasurable testId="gtcc-dplane-inv-databases-unmeasured" />
          ) : (
            <Mono testId="gtcc-dplane-inv-databases-value" style={ROW_VALUE_TEXT_STYLE}>
              {databaseText}
            </Mono>
          )
        }
        testId="gtcc-dplane-inv-databases"
      />
      <FleetRow
        tone={vpnTone}
        label={t("admin.gtcc.dplane.inv.wireguard")}
        value={
          vpn.length === 0 ? (
            <NotMeasurable testId="gtcc-dplane-inv-wireguard-unmeasured" />
          ) : (
            <Mono testId="gtcc-dplane-inv-wireguard-value" style={ROW_VALUE_TEXT_STYLE}>
              {vpnHealthy === vpn.length
                ? t("admin.gtcc.dplane.inv.wireguard.all_fresh")
                : t("admin.gtcc.dplane.inv.wireguard.partial", {
                    healthy: vpnHealthy,
                    count: vpn.length,
                  })}
            </Mono>
          )
        }
        testId="gtcc-dplane-inv-wireguard"
      />
    </Stack>
  );
}

/* -------------------------------------------------- panel: Plan Builders */

function BuilderLinkRow(props: {
  labelKey: string;
  hintKey: string;
  builder: "create" | "assign" | "move";
  testId: string;
  onOpen: (builder: "create" | "assign" | "move") => void;
}): ReactNode {
  const { t } = useLocale();
  return (
    <FleetRow
      tone="info"
      label={t(props.labelKey)}
      value={
        <>
          {/*
           * Hinweis und [Validate] stehen in der Referenz nebeneinander in
           * einer Zeile (`GTCC_REFERENCE_2026-09-01.html:950-952`). Die
           * rechte Haelfte der Zeile baut `FleetRow` mit `ROW_VALUE_STYLE`;
           * hier steht nur noch, was darin nebeneinander liegt. Der Hinweis
           * darf schrumpfen, die Schaltflaeche nie.
           */}
          <Mono style={ROW_VALUE_TEXT_STYLE}>{t(props.hintKey)}</Mono>
          <span style={ROW_ACTION_STYLE}>
            <ControlTextAction
              label={t("admin.gtcc.dplane.builders.validate")}
              onActivate={() => props.onOpen(props.builder)}
              testId={`${props.testId}-validate`}
              /*
               * Alle drei Builder-Zeilen tragen sichtbar dasselbe `[Validate]`
               * wie die Referenz. Der zugaengliche Name nennt zusaetzlich den
               * Plan, damit die Aktionen unterscheidbar bleiben; der sichtbare
               * Text bleibt Teil des Namens.
               */
              ariaLabel={t("admin.gtcc.dplane.builders.validate.aria", {
                plan: t(props.labelKey),
              })}
            />
          </span>
        </>
      }
      testId={props.testId}
    />
  );
}

function PlanHistoryRow(props: {
  plans: ReadonlyArray<DbOpsPersistedPlan>;
  onOpen: () => void;
}): ReactNode {
  const { t } = useLocale();
  const statuses = distinct(props.plans.map((plan) => plan.status)).join(" · ");
  return (
    <FleetRow
      tone={props.plans.length === 0 ? "muted" : "ok"}
      label={t("admin.gtcc.dplane.builders.history")}
      value={
        <>
          {props.plans.length === 0 ? (
            <NotMeasurable testId="gtcc-dplane-builders-history-unmeasured" />
          ) : (
            <Mono testId="gtcc-dplane-builders-history-value" style={ROW_VALUE_TEXT_STYLE}>
              {t("admin.gtcc.dplane.builders.history.value", {
                count: props.plans.length,
                statuses,
              })}
            </Mono>
          )}
          {/*
           * "8 Pläne · validated · [Show timeline]" — die Referenz trennt Wert
           * und Schaltflaeche mit demselben Mittelpunkt wie die Werte
           * untereinander (`GTCC_REFERENCE_2026-09-01.html:953`). Das Zeichen
           * ist reine Zeichensetzung, darum `aria-hidden`: vorgelesen wuerde es
           * den Wert nur zerhacken.
           */}
          <span aria-hidden="true" style={{ ...MONO_STYLE, flex: "0 0 auto" }}>
            ·
          </span>
          <span style={ROW_ACTION_STYLE}>
            <ControlTextAction
              label={t("admin.gtcc.dplane.builders.show_timeline")}
              onActivate={props.onOpen}
              testId="gtcc-dplane-builders-history-timeline"
            />
          </span>
        </>
      }
      testId="gtcc-dplane-builders-history"
    />
  );
}

/* ------------------------------------------------------------------ tab */

export function HealthTab(props: DplaneTabProps): ReactNode {
  const { t, locale } = useLocale();

  const overviewQuery = useQuery({
    queryKey: DPLANE_OVERVIEW_KEY,
    queryFn: ({ signal }) => fetchDplaneOverview(signal),
    ...DPLANE_QUERY_OPTIONS,
  });
  const dbOpsQuery = useQuery({
    queryKey: DPLANE_DBOPS_OVERVIEW_KEY,
    queryFn: ({ signal }) => fetchDplaneDbOpsOverview(signal),
    ...DPLANE_QUERY_OPTIONS,
  });
  const instancesQuery = useQuery({
    queryKey: DPLANE_INSTANCES_KEY,
    queryFn: ({ signal }) => fetchDplaneInstances(signal),
    ...DPLANE_QUERY_OPTIONS,
  });
  const databasesQuery = useQuery({
    queryKey: DPLANE_DATABASES_KEY,
    queryFn: ({ signal }) => fetchDplaneDatabases(signal),
    ...DPLANE_QUERY_OPTIONS,
  });
  const plansQuery = useQuery({
    queryKey: DPLANE_PLANS_KEY,
    queryFn: ({ signal }) => fetchDplanePlans(signal),
    ...DPLANE_QUERY_OPTIONS,
  });

  const formatTime = (iso: string): string => formatBackupMoment(iso, locale, new Date());

  const overviewData = overviewQuery.data?.ok === true ? overviewQuery.data.data : undefined;
  const instanceData = asList(instancesQuery.data?.ok === true ? instancesQuery.data.data : []);
  const databaseData = asList(databasesQuery.data?.ok === true ? databasesQuery.data.data : []);

  return (
    <div style={GRID_MAIN_STYLE} data-testid="gtcc-dplane-health-grid">
      <div style={GRID_COLUMN_STYLE}>
        <DplanePanel
          title={t("admin.gtcc.dplane.health.placement.title")}
          note={t("admin.gtcc.dplane.health.placement.note")}
          testId="gtcc-dplane-placement"
        >
          <Stack gap={2}>
            <DplaneGate
              query={overviewQuery}
              testId="gtcc-dplane-placement-panel"
              render={(data) => (
                <PlacementTable
                  placements={asList(data.tenantPlacements)}
                  datastores={asList(data.datastores)}
                  onMovePlan={(tenantId) => props.onOpenPlanBuilder("move", tenantId)}
                />
              )}
            />
            <p style={SUB_STYLE} data-testid="gtcc-dplane-placement-flow">
              {t("admin.gtcc.dplane.health.placement.flow")}
            </p>
          </Stack>
        </DplanePanel>

        <DplanePanel title={t("admin.gtcc.dplane.health.backup.title")} testId="gtcc-dplane-backup">
          <Stack gap={2}>
            <DplaneGate
              query={overviewQuery}
              testId="gtcc-dplane-backup-panel"
              render={(data) => {
                const backups = asList(data.backup);
                const policies = asList(data.retention);
                return (
                  <Stack gap={0}>
                    <FleetRow
                      tone={worstTone(backups.map((entry) => toneOfBackup(entry.status)))}
                      label={t("admin.gtcc.dplane.health.backup.age")}
                      value={backupValue(backups, formatTime, t)}
                      testId="gtcc-dplane-backup-age"
                    />
                    <FleetRow
                      tone={worstTone(
                        policies.map((policy) => toneOfCleanup(policy.cleanupStatus)),
                      )}
                      label={t("admin.gtcc.dplane.health.retention.label")}
                      value={retentionValue(policies, t)}
                      testId="gtcc-dplane-retention"
                    />
                    <FleetRow
                      tone={worstTone(
                        backups.map((entry) => toneOfRestoreDrill(entry.restoreDrillStatus)),
                      )}
                      label={t("admin.gtcc.dplane.health.restore_drill.label")}
                      value={restoreDrillValue(backups, asList(data.datastores), t)}
                      testId="gtcc-dplane-restore-drill"
                    />
                  </Stack>
                );
              }}
            />
            <EvidenceBundleRow />
          </Stack>
        </DplanePanel>
      </div>

      <div style={GRID_COLUMN_STYLE}>
        <DplanePanel title={t("admin.gtcc.dplane.inv.title")} testId="gtcc-dplane-inv">
          <DplaneGate
            query={dbOpsQuery}
            testId="gtcc-dplane-inv-panel"
            render={(data) => (
              <InventoryRows
                dbOps={data}
                instances={instanceData}
                databases={databaseData}
                overview={overviewData}
              />
            )}
          />
        </DplanePanel>

        <DplanePanel
          title={t("admin.gtcc.dplane.builders.title")}
          note={t("admin.gtcc.dplane.builders.note")}
          testId="gtcc-dplane-builders"
        >
          <Stack gap={0}>
            <BuilderLinkRow
              labelKey="admin.gtcc.dplane.builders.create"
              hintKey="admin.gtcc.dplane.builders.create.hint"
              builder="create"
              testId="gtcc-dplane-builders-create"
              onOpen={(builder) => props.onOpenPlanBuilder(builder)}
            />
            <BuilderLinkRow
              labelKey="admin.gtcc.dplane.builders.assign"
              hintKey="admin.gtcc.dplane.builders.assign.hint"
              builder="assign"
              testId="gtcc-dplane-builders-assign"
              onOpen={(builder) => props.onOpenPlanBuilder(builder)}
            />
            <BuilderLinkRow
              labelKey="admin.gtcc.dplane.builders.move"
              hintKey="admin.gtcc.dplane.builders.move.hint"
              builder="move"
              testId="gtcc-dplane-builders-move"
              onOpen={(builder) => props.onOpenPlanBuilder(builder)}
            />
            <DplaneGate
              query={plansQuery}
              testId="gtcc-dplane-builders-history-panel"
              render={(plans) => (
                <PlanHistoryRow
                  plans={asList(plans)}
                  onOpen={() => props.onOpenPlanBuilder("history")}
                />
              )}
            />
          </Stack>
        </DplanePanel>
      </div>
    </div>
  );
}
